import { SellOutlined as SellOutlinedIcon } from 'components/icons';
import { CustomTableEmptyStateProps } from 'components/tables/list-table/ListTableTypes';
import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import CircularLoader from 'components/CircularLoader';
import Loader from 'components/Loader';
import EmptyState from 'components/EmptyState';
import ScrollX from 'components/ScrollX';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil';
import { EndOfListSentinel } from 'components/tables/EndOfListSentinel';
import BoxLoader from 'components/tables/list-table/loader/BoxLoader';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useCallback, useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetPricelistsCountQuery } from 'store/api/pricelists';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { RelationshipTypeCode } from 'types/contacts';
import { MatterStateType } from 'types/matters';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { CalculatedPricelist, PricelistStateType } from 'types/pricelists';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useClassifiers } from 'utils/useClassifiers';
import { GroupedPricelists, PricelistGroupTable } from './PricelistGroupTable';

const USED_CLASSIFIERS = [
    'CURRENCY',
    'BUSINESS_AREA_TYPE',
    'SEGMENT_TYPE',
    'SERVICE_LEVEL_TYPE',
    'COUNTRY',
    'MATTER_STATE_TYPE',
];

const filterMatterStateTypes = [
    MatterStateType.IN_PROGRESS,
    MatterStateType.OFFER_PENDING,
    MatterStateType.COMPLETED,
    MatterStateType.OFFER_WON,
    MatterStateType.OFFER_LOST,
];

// ==============================|| PRICELISTS TABLE - CUSTOM ||============================== //

interface IProps {
    data?: QueryResponse<CalculatedPricelist>;
    fetchData: LazyQueryTrigger<
        QueryDefinition<
            PaginationProps,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            never,
            QueryResponse<CalculatedPricelist>,
            'portalAPI'
        >
    >;
    isFetching: boolean;
    openEditModal: (editableEntity: CalculatedPricelist) => void;
}

const PricelistsTable = ({ data, fetchData, isFetching, openEditModal }: IProps): JSX.Element => {
    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const matterStateTypeOptions = useMemo(
        () => mapToFilterOptions(filterMatterStateTypes, classifiers['MATTER_STATE_TYPE']),
        [classifiers]
    );

    const renderPricelistsTable = useCallback(
        (
            dataToDisplay: CalculatedPricelist[],
            initialLoad: boolean,
            noData: boolean,
            textId?: string,
            paginationProps?: Partial<QueryResponse<CalculatedPricelist>>,
            handleLoadMore?: () => void,
            emptyStateProps?: CustomTableEmptyStateProps
        ) => {
            const pricelists = dataToDisplay ? Object.values(dataToDisplay).flat() : [];
            const groupedPricelists: Map<number, GroupedPricelists> = new Map();
            pricelists.forEach((pricelist) => {
                const group = groupedPricelists.get(pricelist.clientId || 0);
                if (group) {
                    group.pricelists.push(pricelist);
                } else {
                    groupedPricelists.set(pricelist.clientId || 0, {
                        clientId: pricelist.clientId,
                        clientName: pricelist.clientId ? pricelist.clientName! : 'Model rates',
                        clientFirstName: pricelist.clientFirstName,
                        clientLastName: pricelist.clientLastName,
                        pricelists: [pricelist],
                    });
                }
            });
            const pricelistArray = Array.from(groupedPricelists);

            const isLastPage = paginationProps?.last;
            const rearClientId = [...groupedPricelists.keys()].pop();

            // TODO: PLEASE FIX THIS - useMemo should NOT be inside another hook (useCallback)
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const tableContainerMemoized = useMemo(
                () => (
                    <>
                        {noData ? (
                            <EmptyState
                                size="page"
                                variant={emptyStateProps?.variant ?? 'empty'}
                                icon={SellOutlinedIcon}
                                titleId={textId}
                                bodyId="pricelists.empty.body"
                                surface="pricelists"
                                onClearFilters={emptyStateProps?.onClearFilters}
                                onRetry={emptyStateProps?.onRetry}
                            />
                        ) : (
                            <div className="h-full w-full overflow-x-auto py-1 pr-0.5">
                                {pricelistArray.map(
                                    ([clientId, group]) =>
                                        (isLastPage || clientId < 1 || clientId !== rearClientId) && (
                                            <PricelistGroupTable
                                                key={`client-${clientId}`}
                                                pricelistGroup={group}
                                                openEditModal={openEditModal}
                                            />
                                        )
                                )}

                                {isFetching ? <BoxLoader amountOfColumns={3} height={120} /> : null}
                                {/* Last child of the scroller, so reaching the end of the loaded
                                    groups pulls the next page. Outside the container it would never
                                    be clipped, and so would fire on the first frame. */}
                                <EndOfListSentinel
                                    rowCount={dataToDisplay.length}
                                    hasNextPage={!!paginationProps && !paginationProps.last}
                                    isLoadingMore={isFetching}
                                    onLoadMore={handleLoadMore}
                                />
                            </div>
                        )}
                    </>
                ),
                // `handleLoadMore` owns the page index, so the sentinel rendered in here has to see a
                // fresh one — otherwise it keeps the first closure and never reaches the last page.
                // Block-form disable: oxlint anchors exhaustive-deps diagnostics on individual
                // dependency entries, so a single disable-next-line above the array cannot cover them.
                /* eslint-disable react-hooks/exhaustive-deps */
                [
                    JSON.stringify(dataToDisplay),
                    dataToDisplay.length,
                    rearClientId,
                    isLastPage,
                    isFetching,
                    noData,
                    textId,
                    // The empty state renders inside this memo and `noData` does not change when the
                    // REASON does — an isError that clears over a still-empty result set moves the
                    // variant from 'error' to 'filtered'/'empty', so without this the memo keeps a
                    // stale Retry / Clear filters CTA.
                    emptyStateProps,
                    handleLoadMore,
                ]
                /* eslint-enable react-hooks/exhaustive-deps */
            );

            if (initialLoad) {
                return <Loader />;
            }

            return (
                <ScrollX className="flex">
                    <div className="flex w-full flex-col overflow-y-hidden">
                        <div className="border-divider flex w-full overflow-x-auto overflow-y-hidden rounded-[4px] border-y">
                            {/* z-12 is a local level, not an app tier: it keeps the veil under the list's
                                sticky filter bar (z-30) while covering the groups. */}
                            {isFetching && pricelistArray.length > 0 ? (
                                <CircularLoader className="bg-divider/40 z-12" />
                            ) : null}
                            {tableContainerMemoized}
                        </div>
                    </div>
                </ScrollX>
            );
        },
        [isFetching, openEditModal]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'labelIsValid',
                    chipLabel: 'filters.status',
                    comperator: '=priceliststate=',
                    multiple: true,
                    filters: [
                        { label: t('pricelists.filter.valid'), argument: PricelistStateType.VALID },
                        { label: t('pricelists.filter.expired'), argument: PricelistStateType.EXPIRED },
                        { label: t('pricelists.filter.scheduled'), argument: PricelistStateType.SCHEDULED },
                    ],
                },
                {
                    selector: 'scopeTypeCode',
                    chipLabel: 'pricelists.filter.model-and-scope',
                    comperator: '=priceliststate=',
                    multiple: true,
                    filters: [
                        { label: t('pricelists.filter.model'), argument: PricelistStateType.MODEL },
                        { label: t('pricelists.filter.matches-model'), argument: PricelistStateType.MATCHES_MODEL },
                        { label: t('pricelists.filter.case-based'), argument: PricelistStateType.CASE_BASED },
                        { label: t('pricelists.filter.contract-based'), argument: PricelistStateType.CONTRACT_BASED },
                    ],
                },
            ],
            filters: [
                // -------- GENERAL --------
                {
                    type: 'checkboxgroup',
                    label: t('pricelists.filter.status'),
                    selector: 'labelIsValid',
                    comperator: '=priceliststate=',
                    group: 'pricelists.general',
                    multiple: true,
                    filters: [
                        { label: t('pricelists.filter.valid'), argument: PricelistStateType.VALID },
                        { label: t('pricelists.filter.expired'), argument: PricelistStateType.EXPIRED },
                        { label: t('pricelists.filter.scheduled'), argument: PricelistStateType.SCHEDULED },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    label: t('pricelists.filter.model-and-scope'),
                    selector: 'scopeTypeCode',
                    comperator: '=priceliststate=',
                    group: 'pricelists.general',
                    multiple: true,
                    filters: [
                        { label: t('pricelists.filter.model'), argument: PricelistStateType.MODEL },
                        { label: t('pricelists.filter.matches-model'), argument: PricelistStateType.MATCHES_MODEL },
                        { label: t('pricelists.filter.case-based'), argument: PricelistStateType.CASE_BASED },
                        { label: t('pricelists.filter.contract-based'), argument: PricelistStateType.CONTRACT_BASED },
                    ],
                },
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.currency'),
                    selector: 'currencyTypeCode',
                    group: 'pricelists.general',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                },
                // -------- CLIENT --------
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    label: t('pricelists.filter.client-name'),
                    selector: 'clientId',
                    group: 'pricelists.client',
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        filters: `relationshipTypeCode=in=(${[RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD].join(',')})`,
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.group-name'),
                    selector: 'clientParty.partyGroupParties.partyGroup.id',
                    group: 'pricelists.client',
                    paginated: {
                        fetch: useLazyGetMinGroupsListWithFiltersQuery,
                        searchField: 'groupName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'groupName',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.business-area'),
                    selector: 'clientParty.businessAreas.businessAreaCode',
                    group: 'pricelists.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.sector-group'),
                    selector: 'clientParty.id',
                    comperator: '=hasbusinessareasinsectorgroup=',
                    group: 'pricelists.client',
                    paginated: {
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                        filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.relationship-partner'),
                    selector: 'clientParty.partners.employee.id',
                    group: 'pricelists.client',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.relationship-manager'),
                    selector: 'clientParty.managers.employee.id',
                    group: 'pricelists.client',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.segment'),
                    selector: 'clientParty.segmentTypeCode',
                    group: 'pricelists.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SEGMENT_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.service-level'),
                    selector: 'clientParty.serviceLevelTypeCode',
                    group: 'pricelists.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['SERVICE_LEVEL_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.country'),
                    selector: 'clientParty.addresses.countryCode',
                    group: 'pricelists.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                },
                // -------- CASE -------- (case-based pricelists only, via direct matter join)
                {
                    type: 'multiselect',
                    label: t('pricelists.filter.case-status'),
                    selector: 'matter.matterStateTypeCode',
                    group: 'pricelists.case',
                    options: matterStateTypeOptions,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.handled-by-office'),
                    selector: 'matter.handledByOffice.id',
                    group: 'pricelists.case',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.case-supervised-by'),
                    selector: 'matter.supervisedByEmployee.id',
                    group: 'pricelists.case',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('pricelists.filter.case-responsible-lawyer'),
                    selector: 'matter.responsibleEmployee.id',
                    group: 'pricelists.case',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                // -------- OTHER --------
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=ismyclientpricelist=',
                    group: 'pricelists.other.title',
                    filters: [
                        {
                            label: t('pricelists.filter.my-clients-pricelists'),
                            argument: 'true',
                            tooltip: 'pricelists.tooltips.my-clients-pricelists',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasdiscountedservicecontract=',
                    group: 'pricelists.other.title',
                    filters: [
                        {
                            label: t('pricelists.filter.discounted-service-agreements'),
                            argument: 'true',
                            tooltip: 'pricelists.tooltips.discounted-service-agreements',
                        },
                    ],
                },
            ],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'clientName' },
                { selector: 'companyName' },
                { selector: 'contractName' },
            ],
        }),
        [classifiers, matterStateTypeOptions]
    );

    return (
        <ListTableWithFilters<CalculatedPricelist>
            renderCustomTable={renderPricelistsTable}
            data={data}
            isLoading={isFetching}
            pluralTextId="pricelists.pricelists"
            singularTextId="pricelist"
            noEntriesId="pricelists.no-pricelists-found"
            fetchData={fetchData}
            fetchCount={useLazyGetPricelistsCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default PricelistsTable;
